import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Plus, Trash2, Clock, Calendar, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";

const WEEKDAYS = [
  { value: 0, label: "Seg" },
  { value: 1, label: "Ter" },
  { value: 2, label: "Qua" },
  { value: 3, label: "Qui" },
  { value: 4, label: "Sex" },
  { value: 5, label: "Sáb" },
  { value: 6, label: "Dom" },
];

export default function Schedules() {
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [units, setUnits] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const EMPTY_FORM = {
    template_id: "",
    unit_id: "",
    assignee_id: "",
    frequency: "daily",
    time_of_day: "06:00",
    weekday: 0,
    day_of_month: 1,
  };
  const [form, setForm] = useState(EMPTY_FORM);

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      template_id: s.template_id || "",
      unit_id: s.unit_id || "",
      assignee_id: s.assignee_id || "",
      frequency: s.frequency || "daily",
      time_of_day: s.time_of_day || "06:00",
      weekday: s.weekday ?? 0,
      day_of_month: s.day_of_month ?? 1,
    });
    setOpen(true);
  };

  const load = async () => {
    try {
      const { data } = await api.get("/schedules");
      setItems(data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    load();
    api.get("/checklists/templates").then(({ data }) => setTemplates(data)).catch(() => {});
    api.get("/units").then(({ data }) => setUnits(data)).catch(() => {});
    api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.template_id) {
      toast.error("Selecione um template");
      return;
    }
    try {
      const payload = { ...form };
      if (!payload.unit_id) delete payload.unit_id;
      if (!payload.assignee_id) delete payload.assignee_id;
      if (payload.frequency !== "weekly") delete payload.weekday;
      if (payload.frequency !== "monthly") delete payload.day_of_month;
      if (editing) {
        await api.patch(`/schedules/${editing.id}`, payload);
        toast.success("Agendamento atualizado");
      } else {
        await api.post("/schedules", payload);
        toast.success("Agendamento criado");
      }
      setOpen(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar");
    }
  };

  const toggleActive = async (s) => {
    try {
      await api.patch(`/schedules/${s.id}`, { is_active: !s.is_active });
      load();
    } catch (err) {
      toast.error("Falha ao atualizar");
    }
  };

  const remove = async (id) => {
    if (!confirm("Remover agendamento?")) return;
    try {
      await api.delete(`/schedules/${id}`);
      toast.success("Removido");
      load();
    } catch (err) {
      toast.error("Falha ao remover");
    }
  };

  return (
    <Layout
      title="Agendamentos"
      subtitle="Crie e gerencie execuções automáticas de checklists"
      actions={
        <Dialog
          open={open}
          onOpenChange={(o) => {
            setOpen(o);
            if (!o) setEditing(null);
          }}
        >
          <DialogTrigger asChild>
            <Button
              className="bg-emerald-800 hover:bg-emerald-900 text-white"
              data-testid="schedule-new"
              onClick={() => {
                setEditing(null);
                setForm(EMPTY_FORM);
              }}
            >
              <Plus className="h-4 w-4 mr-2" /> Novo agendamento
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display">
                {editing ? "Editar agendamento" : "Novo agendamento"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Template *</Label>
                <Select value={form.template_id} onValueChange={(v) => setForm({ ...form, template_id: v })}>
                  <SelectTrigger className="mt-1 bg-white" data-testid="schedule-template">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Frequência</Label>
                  <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                    <SelectTrigger className="mt-1 bg-white" data-testid="schedule-freq">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hourly">A cada hora</SelectItem>
                      <SelectItem value="daily">Diário</SelectItem>
                      <SelectItem value="weekly">Semanal</SelectItem>
                      <SelectItem value="monthly">Mensal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Horário (HH:MM)</Label>
                  <Input
                    value={form.time_of_day}
                    onChange={(e) => setForm({ ...form, time_of_day: e.target.value })}
                    className="mt-1 bg-white"
                    placeholder="06:00"
                    data-testid="schedule-time"
                  />
                </div>
              </div>
              {form.frequency === "weekly" && (
                <div>
                  <Label>Dia da semana</Label>
                  <Select value={String(form.weekday)} onValueChange={(v) => setForm({ ...form, weekday: Number(v) })}>
                    <SelectTrigger className="mt-1 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {WEEKDAYS.map((d) => (
                        <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {form.frequency === "monthly" && (
                <div>
                  <Label>Dia do mês</Label>
                  <Input
                    type="number"
                    min="1"
                    max="28"
                    value={form.day_of_month}
                    onChange={(e) => setForm({ ...form, day_of_month: Number(e.target.value) })}
                    className="mt-1 bg-white"
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Unidade</Label>
                  <Select value={form.unit_id || "none"} onValueChange={(v) => setForm({ ...form, unit_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="mt-1 bg-white">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {units.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Responsável</Label>
                  <Select value={form.assignee_id || "none"} onValueChange={(v) => setForm({ ...form, assignee_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="mt-1 bg-white">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submit} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="schedule-submit">
                {editing ? "Salvar alterações" : "Criar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      <Card data-testid="schedules-page" className="rounded-xl border-stone-200 bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="font-body">Template</TableHead>
              <TableHead className="font-body">Frequência</TableHead>
              <TableHead className="font-body">Próxima execução</TableHead>
              <TableHead className="font-body">Última</TableHead>
              <TableHead className="font-body">Ativo</TableHead>
              <TableHead className="font-body text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-stone-500 py-10">
                  <Clock className="h-6 w-6 mx-auto mb-2 text-stone-400" />
                  Nenhum agendamento ativo
                </TableCell>
              </TableRow>
            )}
            {items.map((s) => (
              <TableRow key={s.id} data-testid={`schedule-row-${s.id}`}>
                <TableCell>
                  <button
                    type="button"
                    onClick={() => openEdit(s)}
                    className="font-display font-semibold text-emerald-900 hover:underline inline-flex items-center gap-1.5"
                    data-testid={`schedule-edit-${s.id}`}
                  >
                    {s.template_name || "—"}
                    <Pencil className="h-3 w-3 opacity-40" />
                  </button>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="border-emerald-200 text-emerald-800">
                    {s.frequency} · {s.time_of_day}
                  </Badge>
                </TableCell>
                <TableCell className="font-body text-sm">{formatDateTime(s.next_run_at)}</TableCell>
                <TableCell className="font-body text-sm text-stone-500">{s.last_run_at ? formatDateTime(s.last_run_at) : "—"}</TableCell>
                <TableCell>
                  <Switch checked={s.is_active} onCheckedChange={() => toggleActive(s)} data-testid={`schedule-toggle-${s.id}`} />
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => remove(s.id)}>
                    <Trash2 className="h-4 w-4 text-rose-600" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </Layout>
  );
}
